"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { AdItem } from "@/lib/getAdSpaceData";

export type AdSlot =
  | "header"
  | "index_top"
  | "index_bottom"
  | "post_top"
  | "post_bottom"
  | "sidebar_1"
  | "sidebar_2"
  | "in_article_1"
  | "in_article_2"
  | "posts_top"
  | "posts_bottom"
  | (string & {});

export type Viewport = "auto" | "desktop" | "mobile";

export interface AdSpaceProps {
  slot: AdSlot;
  viewport?: Viewport;
  width?: number | string;
  height?: number | string;
  lang?: number;
  showLabel?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onRender?: (hasAd: boolean) => void;
  initialAds?: AdItem[]; // ← new SSR prop
}

// Client-side cache (fallback when SSR data not provided)
const _cache = new Map<string, { items: AdItem[]; at: number }>();
const _TTL = 5 * 60 * 1000;

async function loadAds(): Promise<AdItem[]> {
  const PROXY = "/api/ad-space";
  const hit = _cache.get(PROXY);
  if (hit && Date.now() - hit.at < _TTL) return hit.items;
  const res = await fetch(PROXY);
  if (!res.ok) throw new Error(`/api/ad-space returned ${res.status}`);
  const json = await res.json();
  const items = (json.data ?? []).flat() as AdItem[];
  _cache.set(PROXY, { items, at: Date.now() });
  return items;
}

function fixAdCode(raw: string): string {
  return raw
    .replace(/src="data:image\/gif[^"]*"\s+data-src="([^"]+)"/g, 'src="$1"')
    .replace(/data-src="([^"]+)"/g, 'src="$1"')
    .replace(/\s*class="lazyload"/g, "")
    .replace(/<a\s+/g, '<a target="_blank" rel="noopener noreferrer" ');
}

const SKELETON_SIZES: Record<string, [number, number]> = {
  header: [1200, 100],
  index_top: [1200, 100],
  index_bottom: [600, 300],
  post_top: [450, 410],
  post_bottom: [750, 150],
  sidebar_1: [370, 370],
  sidebar_2: [406, 200],
  in_article_1: [728, 90],
  in_article_2: [728, 90],
  posts_top: [728, 90],
  posts_bottom: [728, 90],
};

function Skeleton({
  slot,
  width,
  height,
  style,
}: {
  slot: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
}) {
  const [defW, defH] = SKELETON_SIZES[slot] ?? [728, 90];
  return (
    <>
      <style>{`@keyframes _adShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      <div
        aria-hidden
        style={{
          width: width ?? defW,
          height: height ?? defH,
          maxWidth: "100%",
          borderRadius: 8,
          background:
            "linear-gradient(90deg,#ececec 25%,#f7f7f7 50%,#ececec 75%)",
          backgroundSize: "200% 100%",
          animation: "_adShimmer 1.4s ease infinite",
          ...style,
        }}
      />
    </>
  );
}

function toCss(val: number | string | undefined, fallback: string): string {
  if (val === undefined || val === null || val === "") return fallback;
  return typeof val === "number" ? `${val}px` : val;
}

export default function AdSpace({
  slot,
  viewport = "auto",
  lang = 1,
  width,
  height,
  showLabel = false,
  className = "",
  style,
  onRender,
  initialAds, // ← SSR data
}: AdSpaceProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const impressionFired = useRef(false);

  // If SSR data provided — start as "ready", skip loading state entirely
  const findAd = (items: AdItem[]) =>
    items.find((i) => i.ad_space === slot && i.lang_id === String(lang)) ??
    null;

  const ssrAd = useMemo(
    () => (initialAds ? findAd(initialAds) : null),
    [], // intentionally empty — only compute from initial props
  );

  const [ad, setAd] = useState<AdItem | null>(ssrAd);
  const [status, setStatus] = useState<"loading" | "ready" | "empty">(
    initialAds ? (ssrAd ? "ready" : "empty") : "loading",
  );
  const [isMobile, setMobile] = useState(false);

  // Viewport detection
  useEffect(() => {
    if (viewport === "desktop") {
      setMobile(false);
      return;
    }
    if (viewport === "mobile") {
      setMobile(true);
      return;
    }
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [viewport]);

  // Client fetch ONLY when SSR data was not provided
  useEffect(() => {
    if (initialAds) return; // ← skip entirely if SSR data present

    let cancelled = false;
    loadAds()
      .then((items) => {
        if (cancelled) return;
        const match = findAd(items);
        // Populate client cache for other AdSpace instances
        _cache.set("/api/ad-space", { items, at: Date.now() });
        setAd(match);
        setStatus(match ? "ready" : "empty");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[AdSpace] load error:", err);
        setStatus("empty");
      });
    return () => {
      cancelled = true;
    };
  }, [slot, lang, initialAds]);

  // onRender callback
  useEffect(() => {
    if (onRender) onRender(status === "ready" && !!ad);
  }, [status, ad, onRender]);

  // Impression tracking
  useEffect(() => {
    if (status !== "ready" || !ad) return;
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressionFired.current) {
          impressionFired.current = true;
          console.info(`[AdSpace] impression slot=${slot} id=${ad.id}`);
          io.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [status, ad, slot, isMobile]);

  if (status === "loading")
    return <Skeleton slot={slot} width={width} height={height} style={style} />;
  if (status === "empty" || !ad) return null;

  const rawCode = isMobile
    ? ad.ad_code_mobile || ad.ad_code_desktop
    : ad.ad_code_desktop;
  if (!rawCode) return null;

  const adCode = fixAdCode(rawCode);
  const apiW = isMobile ? ad.mobile_width : ad.desktop_width;
  const apiH = isMobile ? ad.mobile_height : ad.desktop_height;
  const resolvedWidth = toCss(width, apiW ? `${apiW}px` : "100%");
  const resolvedHeight = toCss(height, apiH ? `${apiH}px` : "auto");

  return (
    <div
      ref={wrapRef}
      className={`ad-space ad-space--${slot} ${className}`.trim()}
      style={{
        display: "block",
        width: "100%",
        textAlign: "center",
        overflow: "hidden",
        ...style,
      }}
      data-ad-slot={slot}
      data-ad-viewport={isMobile ? "mobile" : "desktop"}
      data-ad-id={ad.id}
    >
      <style>{`.ad-space--${slot} img,.ad-space--${slot} video{height:100%!important}`}</style>
      {showLabel && (
        <span
          style={{
            display: "block",
            fontSize: 9,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#bbb",
            marginBottom: 4,
            fontFamily: "system-ui,sans-serif",
            userSelect: "none",
          }}
        >
          Advertisement
        </span>
      )}
      <div
        style={{
          display: "inline-block",
          width: resolvedWidth,
          height: resolvedHeight,
          maxWidth: "100%",
          overflow: "hidden",
          position: "relative",
        }}
        dangerouslySetInnerHTML={{ __html: adCode }}
      />
    </div>
  );
}
